import { FacebookLogo, XLogo, LinkedinLogo, WhatsappLogo, TelegramLogo, EnvelopeSimple, LinkSimple, InstagramLogo, YoutubeLogo, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";

const NETWORKS = [
  { key: "whatsapp", label: "WhatsApp", Icon: WhatsappLogo, url: (u, t) => `https://wa.me/?text=${encodeURIComponent(`${t} ${u}`)}` },
  { key: "facebook", label: "Facebook", Icon: FacebookLogo, url: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}` },
  { key: "x", label: "X", Icon: XLogo, url: (u, t) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}` },
  { key: "linkedin", label: "LinkedIn", Icon: LinkedinLogo, url: (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}` },
  { key: "telegram", label: "Telegram", Icon: TelegramLogo, url: (u, t) => `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}` },
  { key: "email", label: "Email", Icon: EnvelopeSimple, url: (u, t) => `mailto:?subject=${encodeURIComponent(t)}&body=${encodeURIComponent(u)}` },
];

const BTN = "inline-flex items-center gap-1.5 border border-border bg-white px-2.5 py-2 text-[0.7rem] font-semibold text-slate-600 transition-colors duration-150 hover:border-[#002FA7] hover:text-[#002FA7] md:py-1";

export const ShareBar = ({ url, text = "", testid = "share", compact = false, instagram = false, marketplace = false, youtube = null, onShare }) => {
  const hit = (channel) => { try { onShare?.(channel); } catch { /* counting is never fatal */ } };

  const toMarketplace = async () => {
    hit("marketplace");
    try { await navigator.clipboard.writeText(`${text}\n\n${url}`); } catch { /* clipboard blocked */ }
    window.open("https://www.facebook.com/marketplace/create/item", "_blank", "noopener");
    toast.success("Details and link copied — paste them into your Facebook Marketplace listing");
  };

  const toInstagram = async () => {
    hit("instagram");
    try { await navigator.clipboard.writeText(`${text} ${url}`); } catch { /* clipboard blocked */ }
    if (navigator.share) {
      try { await navigator.share({ text, url }); return; } catch { /* cancelled */ }
    }
    window.open("https://www.instagram.com/", "_blank", "noopener");
    toast.success("Caption and link copied — paste it into your Instagram post or story");
  };

  const toYoutube = async () => {
    hit("youtube");
    const body = youtube?.title ? `${youtube.title}\n\n${youtube.description || ""}` : `${text} ${url}`;
    try { await navigator.clipboard.writeText(body); } catch { /* clipboard blocked */ }
    window.open(youtube?.upload_url || "https://studio.youtube.com/", "_blank", "noopener");
    toast.success("Title and description copied — upload the clip on YouTube and paste them in");
  };

  const copy = async () => {
    hit("link");
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it into Instagram, a DM or anywhere else");
    } catch {
      toast.error("Copy the link from the address bar instead");
    }
  };

  return (
    <div data-testid={testid} className="flex flex-wrap items-center gap-1.5">
      {NETWORKS.map(({ key, label, Icon, url: build }) => (
        <a
          key={key}
          href={build(url, text)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => hit(key)}
          title={`Share on ${label}`}
          aria-label={`Share on ${label}`}
          data-testid={`${testid}-${key}`}
          className={BTN}
        >
          <Icon size={14} weight="fill" />
          {!compact && label}
        </a>
      ))}
      {marketplace && (
        <button type="button" onClick={toMarketplace} title="List it on Facebook Marketplace"
          aria-label="List it on Facebook Marketplace" data-testid={`${testid}-marketplace`}
          className={BTN}>
          <Storefront size={14} weight="fill" />
          {!compact && "Marketplace"}
        </button>
      )}
      {instagram && (
        <button type="button" onClick={toInstagram} title="Share on Instagram"
          aria-label="Share on Instagram" data-testid={`${testid}-instagram`} className={BTN}>
          <InstagramLogo size={14} weight="fill" />
          {!compact && "Instagram"}
        </button>
      )}
      {youtube && (
        <button type="button" onClick={toYoutube} title="Post on YouTube"
          aria-label="Post on YouTube" data-testid={`${testid}-youtube`} className={BTN}>
          <YoutubeLogo size={14} weight="fill" />
          {!compact && "YouTube"}
        </button>
      )}
      <button type="button" onClick={copy} title="Copy link"
        data-testid={`${testid}-copy`} className={BTN}>
        <LinkSimple size={14} />
        {!compact && "Copy link"}
      </button>
    </div>
  );
};
